import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled, { CSSProperties, css, keyframes } from 'styled-components';

import { AnimatedNumber } from '@sorare/core/src/atoms/animations/AnimatedNumber';
import {
  RollingDirection,
  RollingNumber,
} from '@sorare/core/src/atoms/animations/RollingNumber';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineXL, LabelL } from '@sorare/core/src/atoms/typography';
import { RivalsLightning } from '@sorare/core/src/components/rivals/RepScore/RivalsLightning';
import Avatar from '@sorare/core/src/components/user/Avatar';
import { glossary } from '@sorare/core/src/lib/glossary';
import { Color } from '@sorare/core/src/style/types';

import { ProfileOverview_footballRivalsCurrentManager } from './__generated__/index.graphql';

export enum FocusedStatType {
  REP = 'REP',
  RANK = 'RANK',
}

const arrowAppearing = keyframes`
  to {
    top: calc(-1 * var(--arrow-height) / 2.5);
  }
`;
const shadowAppearing = keyframes`
  to {
    opacity: .7;
  }
`;
const flipIn = keyframes`
  to {
    transform: rotateX(0deg);
  }
`;
const focusStat = keyframes`
  from {
    transform: scale(.5);
  }
  to {
    transform: scale(1);
  }
`;

const withPadding = css`
  padding: var(--double-unit) var(--triple-unit);
`;

const Root = styled(Horizontal).attrs<{ focus?: FocusedStatType }>({ gap: 2 })`
  --shadow-color: var(--c-rivals-reputation);

  width: 320px;
  border-radius: var(--unit);
  background-color: var(--c-neutral-100);
  overflow: hidden;

  transform: rotateX(90deg);

  animation: 0.4s cubic-bezier(0.34, 0.33, 0.19, 1) 0.4s ${flipIn} forwards;

  &::before {
    content: '';
    position: absolute;
    z-index: -1;
    inset: 0;
    opacity: 0;

    ${({ focus }) => {
      if (focus === FocusedStatType.RANK) {
        return css`
          background:
            radial-gradient(
                farthest-corner at left center,
                var(--shadow-color) 0%,
                transparent 30%
              )
              left center / 60% 500% no-repeat,
            var(--c-neutral-100);
        `;
      }
      return css`
        background:
          radial-gradient(
              farthest-corner at right center,
              var(--shadow-color) 0%,
              transparent 30%
            )
            right center / 100% 500% no-repeat,
          var(--c-neutral-100);
      `;
    }}

    animation: 0.8s cubic-bezier(0.34, 0.33, 0.19, 1) 1s ${shadowAppearing}
      forwards;
  }
`;
const User = styled(Horizontal).attrs({ center: true })`
  flex: 1;
  ${withPadding}
`;
const FocusedStat = styled(Horizontal).attrs({ gap: 0, center: true })`
  --arrow-height: var(--intermediate-unit);

  position: relative;
  text-shadow: none;

  &::before {
    content: '';
    position: absolute;
    z-index: 2;
    top: calc(-2 * var(--arrow-height));
    left: calc(50% - var(--arrow-height));
    width: 0;
    height: 0;
    border: var(--arrow-height) solid transparent;
    border-top: var(--arrow-height) solid var(--shadow-color);

    animation: ${arrowAppearing} 0.8s ease-in-out forwards;
    animation-delay: 1s;
  }
`;
const RepWrapper = styled(Horizontal).attrs({ gap: 0 })`
  width: 100px;
  align-items: baseline;
  justify-content: flex-end;
  ${withPadding}
`;
const ShadowedText = styled(HeadlineXL)`
  display: block;
  transform: scale(0.5);
  animation: ${focusStat} 0.4s ease-in-out forwards;
  animation-delay: 1s;
`;
const RankWrapper = styled.div`
  padding: 0 var(--double-unit);
`;

type Props = {
  rankBefore: number;
  rankAfter: number;
  repBefore: number;
  repAfter: number;
  manager: ProfileOverview_footballRivalsCurrentManager;
  focus?: FocusedStatType;
  shadowColor?: Color;
};
export const ProfileOverview = ({
  rankBefore,
  rankAfter,
  repBefore,
  repAfter,
  manager,
  shadowColor,
  focus,
}: Props) => {
  if (!manager.user) {
    return null;
  }

  const rep = (
    <RepWrapper>
      <AnimatedNumber
        value={repAfter}
        animationProps={{
          from: { animatedValue: repBefore },
          config: {
            duration: 800,
          },
          delay: 1800,
        }}
      />
      <RivalsLightning fill="var(--c-rivals-reputation)" />
    </RepWrapper>
  );

  return (
    <Root
      focus={focus}
      style={
        {
          '--shadow-color': shadowColor,
        } as CSSProperties
      }
    >
      {focus === FocusedStatType.RANK ? (
        <FocusedStat>
          <ShadowedText>
            <RollingNumber
              before={rankBefore}
              after={rankAfter}
              initialDelay="1.8s"
              rollingDirection={
                rankAfter < rankBefore
                  ? RollingDirection.DOWN
                  : RollingDirection.UP
              }
            />
          </ShadowedText>
        </FocusedStat>
      ) : (
        <RankWrapper>
          <LabelL bold>{rankAfter}</LabelL>
        </RankWrapper>
      )}
      <User>
        <Avatar user={manager.user} rounded />
        <LabelL>
          <FormattedMessage {...glossary.you} />
        </LabelL>
      </User>
      {focus === FocusedStatType.REP ? (
        <FocusedStat>
          <ShadowedText>{rep}</ShadowedText>
        </FocusedStat>
      ) : (
        rep
      )}
    </Root>
  );
};

ProfileOverview.fragments = {
  footballRivalsCurrentManager: gql`
    fragment ProfileOverview_footballRivalsCurrentManager on FootballRivalsCurrentManager {
      id
      user {
        slug
        ...Avatar_user
      }
    }
    ${Avatar.fragments.user}
  ` as TypedDocumentNode<ProfileOverview_footballRivalsCurrentManager>,
};
